import { IButtonStyles, IStackItemStyles, IStackStyles, getTheme, keyframes, mergeStyleSets } from '@fluentui/react';
import { ITextField } from '@fluentui/react/lib/TextField';
import AppContext from 'App/AppContext';
import { IPIconButton, IPText } from 'baseComponents';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import { AdaptiveSubmit, JsonObject } from 'components/AdaptiveCard/AdaptiveCardRenderer';
import AIFeedback, { IAIFeedback } from 'components/AI/AIFeedback';
import HrefLink from 'components/Links/HrefLink';
import { cancelIcon, checkIcon, editIcon, globalStackTokensGapSmall } from 'globalStyles';
import {
  CopilotConversationContentBlock,
  CopilotConversationItem,
  CopilotConversionItemType,
  CopilotLibraryDocumentEditorRequest,
} from 'models/copilot/copilotConversation';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatElapsed, thinkingElapsedMinMs, thoughtForMinMs, toLocaleDateTimeShort } from 'utils/datetime';
import { toNameInitial } from 'utils/string';
import { SharepointIntegrationsMessage } from '../../Library/SharepointIntegrationsMessage';
import { SkillControlAccessCard } from '../../AutomationDrafting/SkillControlAccessCard';
import { CopilotContentBlockRenderer } from './CopilotContentBlockRenderer';
import { CopilotMarkdown } from './CopilotMarkdown';
import CopilotTypingIndicator from './CopilotTypingIndicator';

interface ICopilotDataChatItemProps {
  item: CopilotConversationItem;
  sessionId?: number;
  isStreaming?: boolean;
  onEntitySelection: (selection: number[]) => void;
  isMostRecent?: boolean;
  pendingQuestionBlockId?: string;
  onAdaptiveInputsChanged?: (blockId: string | undefined, inputs: Record<string, string>) => void;
  onAdaptiveSubmit?: (block: CopilotConversationContentBlock, payload: AdaptiveSubmit<JsonObject>) => void;
  onApprovalDetails?: (block: CopilotConversationContentBlock, payload: AdaptiveSubmit<JsonObject>) => void;
  onOpenLibraryDocumentEditor?: (request: CopilotLibraryDocumentEditorRequest) => void | Promise<void>;
  canEditLastQuestion?: boolean;
  onEditLastQuestion?: (question: string) => void | Promise<void>;
  /** The verdict on this answer, for the host to keep. Absent leaves the thumbs out of the bubble. */
  onFeedback?: (feedback: IAIFeedback) => void;
  /** False for a bubble the host made itself, such as the welcome message: those are not answers. */
  canGiveFeedback?: boolean;
  /** True in the annual plan, whose older selection items were stored without their question. */
  isAnnualPlan?: boolean;
  /**
   * The date of the request this item answers: the streaming item counts the Thinking time from it, the turn's
   * answer shows "Thought for" from it to its own date. Absent shows neither.
   */
  requestDate?: Date;
}

export const CopilotChatItem = (props: ICopilotDataChatItemProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'copilot']);
  const theme = getTheme();
  const [isEditingQuestion, setIsEditingQuestion] = useState<boolean>(false);
  const [draftQuestion, setDraftQuestion] = useState<string>(props.item.question ?? '');
  const editTextFieldRef = useRef<ITextField | null>(null);

  useEffect(() => {
    if (isEditingQuestion) {
      editTextFieldRef.current?.focus();
    }
  }, [isEditingQuestion]);

  useEffect(() => {
    if (!isEditingQuestion) {
      setDraftQuestion(props.item.question ?? '');
    }
  }, [isEditingQuestion, props.item.question]);

  const renderItemDate = (color: string = theme.palette.neutralSecondaryAlt) => (
    <IPText variant="small" styles={{ root: { color } }}>
      {toLocaleDateTimeShort(props.item.date)}
    </IPText>
  );

  // The answer's meta line: the time stamp and, for a turn of 3 seconds or more, how long the assistant took
  const renderAnswerDate = () => {
    const color = theme.palette.neutralSecondaryAlt;
    const elapsed =
      props.requestDate && props.item.date ? props.item.date.getTime() - props.requestDate.getTime() : undefined;

    return (
      <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
        {renderItemDate(color)}
        {elapsed !== undefined && elapsed >= thoughtForMinMs && (
          <IPText variant="small" styles={{ root: { color } }}>
            {t('copilot:Chat.ThoughtFor', { elapsed: formatElapsed(elapsed, t) })}
          </IPText>
        )}
      </IPStack>
    );
  };

  const renderResponse = (markdownWithTableStyles: boolean = false) => {
    const hasBlocks = props.item.contentBlocks?.some((block) => !!block) === true;

    return (
      <IPStack tokens={{ childrenGap: 8 }}>
        {props.item.response && (
          <IPStack.Item>
            <CopilotMarkdown content={props.item.response} withTableStyles={markdownWithTableStyles} />
          </IPStack.Item>
        )}
        {hasBlocks && (
          <IPStack.Item>
            <CopilotContentBlockRenderer
              blocks={props.item.contentBlocks}
              sessionId={props.sessionId}
              markdownWithTableStyles={markdownWithTableStyles}
              isMostRecent={props.isMostRecent}
              pendingQuestionBlockId={props.pendingQuestionBlockId}
              onAdaptiveInputsChanged={props.onAdaptiveInputsChanged}
              onAdaptiveSubmit={props.onAdaptiveSubmit}
              onEntitySelection={props.onEntitySelection}
              onApprovalDetails={props.onApprovalDetails}
              onOpenLibraryDocumentEditor={props.onOpenLibraryDocumentEditor}
            />
          </IPStack.Item>
        )}
      </IPStack>
    );
  };

  //
  // A finished Copilot answer is the only bubble that can be judged: it is the one the model wrote
  // and the one the session kept under its own id. A client-made bubble (the welcome message, a
  // refusal with a button) has no id in the session, so there is nothing to hang a verdict from.
  //
  const renderFeedback = (): JSX.Element | null => {
    const onFeedback = props.onFeedback;
    const answerKey = props.item.id;
    if (onFeedback === undefined || props.canGiveFeedback === false) return null;
    if (props.item.type !== CopilotConversionItemType.Copilot) return null;
    if (!props.item.response || !answerKey || props.item.action !== undefined) return null;

    return (
      <IPStack.Item>
        <IPStack horizontal horizontalAlign="end">
          <AIFeedback answerKey={answerKey} onFeedback={onFeedback} />
        </IPStack>
      </IPStack.Item>
    );
  };

  const getChatItemBackground = (itemType: CopilotConversionItemType): string => {
    switch (itemType) {
      case CopilotConversionItemType.Copilot:
      case CopilotConversionItemType.CopilotQuestion:
        return appContext.useDarkMode ? theme.palette.neutralDark : theme.palette.themeLighterAlt;
      case CopilotConversionItemType.User:
        return appContext.useDarkMode ? '#3d5a3f' : '#c1e9c4';
      default:
        return appContext.useDarkMode ? theme.palette.themeDarker : theme.palette.themeLighterAlt;
    }
  };

  //
  // Main render
  //
  const bgColor = getChatItemBackground(props.item.type);
  const chatRowStyles: IStackStyles = { root: { minWidth: 0, width: '100%' } };
  const chatBubbleStyles: IStackItemStyles = {
    root: {
      background: bgColor,
      borderRadius: 5,
      maxWidth: 'calc(100% - 50px)',
      minWidth: 0,
      overflow: 'hidden',
      overflowWrap: 'anywhere',
      padding: 8,
    },
  };
  const editButtonStyles: IButtonStyles = {
    root: {
      height: 24,
      minHeight: 24,
      minWidth: 24,
      padding: 0,
      width: 24,
    },
    icon: {
      fontSize: 12,
    },
  };
  const classNames = mergeStyleSets({
    editableBubble: {
      selectors: {
        '.copilot-chat-edit-action': {
          opacity: 0,
          transition: 'opacity 120ms ease',
        },
        '&:focus-within .copilot-chat-edit-action, &:hover .copilot-chat-edit-action': {
          opacity: 1,
        },
      },
    },
  });

  const submitEditedQuestion = async () => {
    const trimmedQuestion = draftQuestion.trim();
    if (!trimmedQuestion) return;

    setIsEditingQuestion(false);
    await props.onEditLastQuestion?.(trimmedQuestion);
  };

  const onEditedQuestionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    if ((e.nativeEvent as KeyboardEvent).isComposing) return;
    if (e.shiftKey) return;

    e.preventDefault();
    void submitEditedQuestion();
  };

  const renderQuestionContent = () => {
    if (isEditingQuestion) {
      return (
        <IPStack tokens={{ childrenGap: 6 }}>
          <IPStack.Item>
            <IPTextField
              componentRef={editTextFieldRef}
              multiline
              autoAdjustHeight
              hideChrome
              value={draftQuestion}
              onChange={(_, value) => setDraftQuestion(value ?? '')}
              onKeyDown={onEditedQuestionKeyDown}
            />
          </IPStack.Item>
          <IPStack.Item>
            <IPStack horizontal horizontalAlign="end" tokens={{ childrenGap: 4 }}>
              <IPIconButton
                title={t('translation:General.Button.Cancel')}
                iconProps={cancelIcon}
                styles={editButtonStyles}
                onClick={() => setIsEditingQuestion(false)}
              />
              <IPIconButton
                title={t('translation:General.Button.Send')}
                iconProps={checkIcon}
                styles={editButtonStyles}
                disabled={!draftQuestion.trim()}
                onClick={() => void submitEditedQuestion()}
              />
            </IPStack>
          </IPStack.Item>
        </IPStack>
      );
    }

    return (
      <IPStack horizontal verticalAlign="start" tokens={{ childrenGap: 4 }}>
        <IPStack.Item grow>{props.item.question && <IPText>{props.item.question}</IPText>}</IPStack.Item>
        {props.canEditLastQuestion && (
          <IPStack.Item>
            <IPIconButton
              className="copilot-chat-edit-action"
              title={t('translation:General.Button.Edit')}
              iconProps={editIcon}
              styles={editButtonStyles}
              onClick={() => setIsEditingQuestion(true)}
            />
          </IPStack.Item>
        )}
      </IPStack>
    );
  };

  switch (props.item.type) {
    case CopilotConversionItemType.CopilotQuestion:
    case CopilotConversionItemType.Copilot:
      if (props.isStreaming) {
        const hasResponse = !!props.item.response;

        return (
          <IPStack horizontal horizontalAlign="start" tokens={globalStackTokensGapSmall} styles={chatRowStyles}>
            <IPStack.Item>
              {hasResponse ? (
                <IPPersona
                  hidePersonaDetails={true}
                  size={IPPersonaSize.size40}
                  imageInitials={'AI'}
                  initialsColor={'green'}
                />
              ) : (
                <CopilotTypingIndicator />
              )}
            </IPStack.Item>
            <IPStack.Item styles={chatBubbleStyles}>
              <IPStack>
                <IPStack.Item>{renderItemDate()}</IPStack.Item>
                {hasResponse && <IPStack.Item>{renderResponse(true)}</IPStack.Item>}
                {props.isStreaming && props.item.copilotStatus && (
                  <IPStack.Item styles={{ root: { paddingBottom: 5 } }}>
                    <CopilotChatItemStatus status={props.item.copilotStatus} since={props.requestDate} />
                  </IPStack.Item>
                )}
                {hasResponse && (
                  <IPStack.Item>
                    <CopilotTypingIndicator />
                  </IPStack.Item>
                )}
              </IPStack>
            </IPStack.Item>
          </IPStack>
        );
      } else {
        return (
          <IPStack horizontal horizontalAlign="start" tokens={globalStackTokensGapSmall} styles={chatRowStyles}>
            <IPStack.Item>
              <IPPersona
                hidePersonaDetails={true}
                size={IPPersonaSize.size40}
                imageInitials={'AI'}
                initialsColor={'green'}
              />
            </IPStack.Item>
            <IPStack.Item styles={chatBubbleStyles}>
              <IPStack>
                <IPStack.Item>{renderAnswerDate()}</IPStack.Item>
                <IPStack.Item>{renderResponse(true)}</IPStack.Item>
                {renderFeedback()}
              </IPStack>
            </IPStack.Item>
          </IPStack>
        );
      }

    case CopilotConversionItemType.User:
      return (
        <IPStack horizontal horizontalAlign="end" tokens={globalStackTokensGapSmall} styles={chatRowStyles}>
          <IPStack.Item
            styles={chatBubbleStyles}
            className={props.canEditLastQuestion ? classNames.editableBubble : undefined}
          >
            <IPStack>
              <IPStack.Item>{renderItemDate()}</IPStack.Item>
              <IPStack.Item>
                {renderQuestionContent()}
                {props.item.selectedEntityIds && !props.item.question && (
                  <IPText>
                    {props.isAnnualPlan
                      ? t('copilot:Processes.AnnualPlan.Selection', { count: props.item.selectedEntityIds.length })
                      : t('copilot:Chat.SelectedItems', { count: props.item.selectedEntityIds.length })}
                  </IPText>
                )}
              </IPStack.Item>
            </IPStack>
          </IPStack.Item>
          <IPStack.Item>
            <IPPersona
              imageUrl={appContext.user.getPhotoURLObj()}
              hidePersonaDetails={true}
              size={IPPersonaSize.size40}
              imageInitials={toNameInitial(appContext.user.name)}
            />
          </IPStack.Item>
        </IPStack>
      );
    case CopilotConversionItemType.CopilotAnswerWithLink:
      return (
        <IPStack horizontal horizontalAlign="start" tokens={globalStackTokensGapSmall} styles={chatRowStyles}>
          <IPStack.Item>
            <IPPersona
              hidePersonaDetails={true}
              size={IPPersonaSize.size40}
              imageInitials={'AI'}
              initialsColor={'green'}
            />
          </IPStack.Item>
          <IPStack.Item styles={chatBubbleStyles}>
            <IPStack>
              <IPStack.Item>{renderItemDate(theme.palette.neutralSecondary)}</IPStack.Item>
              <IPStack.Item>{renderResponse()}</IPStack.Item>
              <IPStack.Item>
                <HrefLink href={props.item.link}> {props.item.link}</HrefLink>
              </IPStack.Item>
            </IPStack>
          </IPStack.Item>
        </IPStack>
      );
    case CopilotConversionItemType.SharepointIntegrations:
      return <SharepointIntegrationsMessage item={props.item} />;
    case CopilotConversionItemType.SkillControlAccess:
      return <SkillControlAccessCard item={props.item} />;
    default:
      return null;
  }
};

// Copilot chat item status
interface ICopilotChatItemStatusProps {
  status: string;
  // the request's date: the line counts the turn from it
  since?: Date;
}

const CopilotChatItemStatus = (props: ICopilotChatItemStatusProps) => {
  const { t } = useTranslation(['copilot']);
  const theme = getTheme();
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    if (!props.since) return undefined;

    const intervalId = window.setInterval(() => setNow(Date.now()), 1000);

    return () => window.clearInterval(intervalId);
  }, [props.since]);

  const createStyles = (duration: number, baseColor: string, highlightColor: string) => {
    const shimmer = keyframes({
      '0%': { backgroundPosition: '100% 0' },
      '100%': { backgroundPosition: '0 0' },
    });

    return mergeStyleSets({
      text: {
        display: 'inline-block',
        color: baseColor,
        backgroundImage: `linear-gradient(100deg, ${baseColor} 0%, ${baseColor} 40%, ${highlightColor} 50%, ${baseColor} 60%, ${baseColor} 100%)`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '220% 100%',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        animationName: shimmer,
        animationDuration: `${duration}s`,
        animationTimingFunction: 'linear',
        animationIterationCount: 'infinite',
      },
    });
  };

  const styles = createStyles(2, theme.palette.neutralSecondary, theme.palette.themeLight);

  // the running line counts the turn from the request's date once it has taken 10 seconds
  const elapsed = props.since ? now - props.since.getTime() : 0;
  const text =
    elapsed >= thinkingElapsedMinMs
      ? t('copilot:Chat.ThinkingElapsed', { status: props.status, elapsed: formatElapsed(elapsed, t) })
      : props.status;

  return <IPText className={styles.text}>{text}</IPText>;
};
